import React from "react";
import { useI18n } from "@core/i18n/i18n";
import { analyticsService, getStartedManager } from "@core/service-container";
import styled from "@emotion/styled";
import { row, stack } from "@ui/style/layout";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { ClickableText } from "@ui/components/common/clickable-text";
import { useExercisePage } from "@ui/pages/exercise/hooks/use-exercise-page";
import { useExerciseFilters } from "@ui/components/exercise/hooks/use-exercise-filters";
import { FilterItem, FilterItemStatus } from "@ui/components/filters/filter-item";
import { ActiveFilterListing } from "@ui/pages/exercise/components/active-filter-listing";
import { AnalyticsCategory } from "@domain/analytics/analytics-types";

interface FilterListingPanelProps {
	className?: string;
}

export const FilterListingPanel: React.FC<FilterListingPanelProps> = ({ className }) => {
	const { format } = useI18n();
	const { isDesktop } = useDimensions();
	const { activeFiltersMap, clearAllActiveFilters, updateActiveFilters } = useExercisePage();
	const filters = useExerciseFilters();

	return (
		<FilterPanelContainer className={className}>
			<TagListContainer>
				<Header>
					<div className="p-size-l-bold">{format("exerciseFilterListingPanel.title")}</div>
					{activeFiltersMap.size > 0 && (
						<ClickableText onClick={clearAllActiveFilters}>{format("exerciseFilterListingPanel.clear")}</ClickableText>
					)}
				</Header>
				{isDesktop && <ActiveFilterListing />}
			</TagListContainer>
			{filters.map(category => (
				<CategoryContainer key={category.id}>
					<div className="p-size-m-bold">{category.label}</div>
					<FilterItemListContainer>
						{category.filters.map(filter => (
							<FilterItem
								key={filter.id}
								filter={filter}
								status={
									activeFiltersMap.get(category.id)?.filters.get(filter.id)
										? FilterItemStatus.Active
										: FilterItemStatus.Inactive
								}
								onChange={async isActive => {
									updateActiveFilters(category, filter, isActive);
									await getStartedManager.tryMarkExercisesStepAsCompleted();
									isActive &&
										analyticsService.trackEvent({
											category: AnalyticsCategory.EXERCISE,
											action: "filterLibrary",
											params: [`${category.id}/${filter.id.toLowerCase()}`],
										});
								}}
							/>
						))}
					</FilterItemListContainer>
				</CategoryContainer>
			))}
		</FilterPanelContainer>
	);
};

const FilterPanelContainer = styled.div`
	${stack(16, undefined, "stretch")}
`;

const TagListContainer = styled.div`
	${stack("XS", undefined, "stretch")}
`;

const Header = styled.div`
	${row(undefined, "spacebetween", "center")}
`;

const CategoryContainer = styled.div`
	${stack("XS")}
`;

const FilterItemListContainer = styled.div`
	${row("XS")}
	flex-wrap: wrap;
`;
